import { CartUpdateEvent, ThemeEvents } from '@theme/events';

// ─────────────────────────────────────────────
// DoppelHangers cart logic
// ─────────────────────────────────────────────
// Every "N Hangers" variant is a whole bundle, so the cart should
// hold ONE DoppelHangers line at quantity 1 per color (White / Black).
// This script:
//  • powers the hanger stepper + "Upgrade" button (swap to the same
//    color's N-Hangers variant instead of multiplying the bundle), and
//  • merges stray lines of the same color (e.g. a White 4-pack added
//    twice, or a White single + a White 4-pack) into the matching
//    single bundle — always the same or a lower price, since
//    per-hanger price only goes down. Different colors stay separate.
//  • powers the White | Black switch on each line (moves that line's
//    hangers to the other color, merging with it if already in the cart).
// After each change it fires Horizon's CartUpdateEvent so the drawer,
// cart page and bubble re-render themselves.

const HANDLE = 'doppelhangers';
const SOURCE = 'dh-cart';

if (!window.__dhCartInit) {
  window.__dhCartInit = true;

  let busy = false;

  const readVariantMap = () => {
    const el = document.querySelector('[data-dh-variant-map]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[DH cart] Could not parse variant map:', err);
      return null;
    }
  };

  // Map shape: { qtyIndex, colorIndex, variants: { [color]: { [hangers]: {id, price, available} } } }
  // colorIndex is -1 (single color key "") when the product has no Color option.
  const optionAt = (item, index) => (index >= 0 && item.variant_options ? item.variant_options[index] : undefined);

  const hangersIn = (item, map) =>
    parseInt(optionAt(item, map ? map.qtyIndex : 0) ?? item.variant_title, 10) || 0;

  const colorOf = (item, map) => (map && map.colorIndex >= 0 ? optionAt(item, map.colorIndex) || '' : '');

  const hasVisibleProperties = (item) =>
    Object.entries(item.properties || {}).some(([key, value]) => value && !key.startsWith('_'));

  const bundleLines = (cart, map) =>
    cart.items.filter((item) => item.handle === HANDLE && hangersIn(item, map) > 0 && !hasVisibleProperties(item));

  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const setLoading = (on) => {
    document.querySelectorAll('cart-items-component').forEach((el) => el.classList.toggle('is-dh-loading', on));
  };

  const entryFor = (map, color, hangers) =>
    map && map.variants && map.variants[color] ? map.variants[color][String(hangers)] : undefined;

  // Replace every DoppelHangers line of this color with a single
  // "target Hangers" variant of the same color.
  async function setHangers(target, color = '') {
    if (busy) return;
    const map = readVariantMap();
    const entry = entryFor(map, color, target);
    if (!entry || !entry.available) return;

    busy = true;
    setLoading(true);
    try {
      const cart = await getCart();
      const updates = {};
      let kept = false;

      bundleLines(cart, map).forEach((item) => {
        if (colorOf(item, map) !== color) return;
        if (!kept && item.variant_id === entry.id) {
          updates[item.key] = 1;
          kept = true;
        } else {
          updates[item.key] = 0;
        }
      });
      if (!kept) updates[entry.id] = 1;

      await commit(updates);
    } catch (err) {
      console.error('[DH cart]', err);
    } finally {
      busy = false;
      setLoading(false);
    }
  }

  // Every section that shows the cart (drawer, cart page). Their fresh HTML
  // comes back with the cart change itself, so the drawer can never show a
  // stale total (the theme's own re-render reuses in-flight requests).
  const cartSectionIds = () =>
    Array.from(new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId)));

  async function commit(updates) {
    const sections = cartSectionIds();
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);

    const updated = await res.json();
    document.dispatchEvent(
      new CartUpdateEvent(updated, SOURCE, { source: SOURCE, itemCount: updated.item_count, sections: updated.sections })
    );
  }

  window.dhCartSectionIds = cartSectionIds;

  // Move every hanger of color `from` to color `to` (one bundle line).
  async function recolor(from, to) {
    if (busy || from === to) return;
    const map = readVariantMap();
    if (!map || !map.variants || !map.variants[to]) return;

    busy = true;
    setLoading(true);
    try {
      const lines = bundleLines(await getCart(), map);
      const fromLines = lines.filter((item) => colorOf(item, map) === from);
      const toLines = lines.filter((item) => colorOf(item, map) === to);
      const sum = (list) => list.reduce((n, item) => n + hangersIn(item, map) * item.quantity, 0);

      const target = sum(fromLines) + sum(toLines);
      const entry = entryFor(map, to, target);
      if (!fromLines.length || !entry || !entry.available) return;

      const updates = {};
      let kept = false;
      fromLines.forEach((item) => { updates[item.key] = 0; });
      toLines.forEach((item) => {
        if (!kept && item.variant_id === entry.id && item.quantity === 1) {
          updates[item.key] = 1;
          kept = true;
        } else {
          updates[item.key] = 0;
        }
      });
      if (!kept) updates[entry.id] = 1;

      await commit(updates);
    } catch (err) {
      console.error('[DH cart]', err);
    } finally {
      busy = false;
      setLoading(false);
    }
  }

  // Merge multiple / multiplied DoppelHangers lines into one bundle per color.
  async function reconcile() {
    if (busy) return;
    try {
      const map = readVariantMap();
      if (!map || !map.variants) return;

      const byColor = {};
      bundleLines(await getCart(), map).forEach((item) => {
        const color = colorOf(item, map);
        (byColor[color] = byColor[color] || []).push(item);
      });

      for (const [color, lines] of Object.entries(byColor)) {
        if (lines.length === 1 && lines[0].quantity === 1) continue;

        const total = lines.reduce((sum, item) => sum + hangersIn(item, map) * item.quantity, 0);
        const entry = entryFor(map, color, total);
        if (!entry || !entry.available) continue;

        await setHangers(total, color);
      }
    } catch (err) {
      console.error('[DH cart]', err);
    }
  }

  // White | Black switch on a cart line.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-dh-recolor]') : null;
    if (!button) return;
    event.preventDefault();
    const fromEl = button.closest('[data-dh-color]');
    if (fromEl) recolor(fromEl.getAttribute('data-dh-color'), button.getAttribute('data-dh-recolor'));
  });

  // Stepper (−/+) and upsell "Upgrade" buttons.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-dh-set]') : null;
    if (!button || button.disabled) return;
    event.preventDefault();
    const target = parseInt(button.getAttribute('data-dh-set'), 10);
    const colorEl = button.closest('[data-dh-color]');
    const color = colorEl ? colorEl.getAttribute('data-dh-color') : '';
    if (target > 0) setHangers(target, color);
  });

  // Anything else that changes the cart → check whether lines need merging.
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    setTimeout(reconcile, 50);
  });

  // The product page adds to cart outside Horizon's events and then opens
  // the drawer, so also check whenever the drawer opens.
  const dialog = document.querySelector('.cart-drawer__dialog');
  if (dialog) {
    new MutationObserver(() => {
      if (dialog.hasAttribute('open')) reconcile();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  reconcile();
}
